/**
 * Search Utility Functions
 * 
 * Utilities for implementing multi-term search functionality and search result highlighting.
 */

import React from 'react';
import { globalSearchConfig } from './global-search-config';

/**
 * Split search query into individual terms and check if all terms match in the record
 * 
 * @param searchQuery - The search string to split and match
 * @param record - The record object to search in
 * @param searchableFields - Array of field names to search in
 * @param caseSensitive - Whether search should be case sensitive
 * @returns true if all search terms are found somewhere in the searchable fields
 */
export function matchesAllTerms(
  searchQuery: string,
  record: Record<string, any>,
  searchableFields: string[],
  caseSensitive: boolean = false
): boolean {
  if (!searchQuery.trim()) {return true;}
  
  // Split search query by spaces and filter out empty strings
  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  
  // Get all searchable field values as strings
  const searchableText = searchableFields
    .map(field => {
      const value = record[field];
      return value != null ? String(value) : '';
    })
    .join(' ');
  
  const textToSearch = caseSensitive ? searchableText : searchableText.toLowerCase();
  
  // Check if ALL terms are found somewhere in the searchable text
  const result = searchTerms.every(term => {
    const termToMatch = caseSensitive ? term : term.toLowerCase();
    return textToSearch.includes(termToMatch);
  });
  
  return result;
}

/**
 * Highlight search terms in text by wrapping them with a span element
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns React element array with highlighted terms
 */
export function highlightSearchTerms(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): React.ReactElement[] | string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  // Use provided options or fall back to global config
  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Split text by matches and create elements
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    // Check if this part matches any search term
    const isMatch = searchTerms.some(term => {
      const termToMatch = caseSensitive ? term : term.toLowerCase();
      const partToMatch = caseSensitive ? part : part.toLowerCase();
      return termToMatch === partToMatch;
    });

    if (isMatch) {
      return React.createElement(
        'span',
        {
          key: index,
          className,
          style,
        },
        part
      );
    }
    return part;
  }) as React.ReactElement[];
}

/**
 * Highlight search terms in text and return as HTML string
 * Useful for non-React contexts or when dangerouslySetInnerHTML is needed
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns HTML string with highlighted terms
 */
export function highlightSearchTermsHTML(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Convert style object to CSS string
  const styleString = style
    ? Object.entries(style)
        .map(([key, value]) => {
          // Convert camelCase to kebab-case
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${cssKey}: ${value}`;
        })
        .join('; ')
    : '';

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Replace matches with highlighted spans
  return text.replace(regex, (match) => {
    const classAttr = className ? `class="${className}"` : '';
    const styleAttr = styleString ? `style="${styleString}"` : '';
    const attrs = [classAttr, styleAttr].filter(Boolean).join(' ');
    return `<span ${attrs}>${match}</span>`;
  });
}

/**
 * Escape special regex characters in a string
 * 
 * @param string - The string to escape
 * @returns Escaped string safe for use in regex
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}